import { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Polygon, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import "../styles/GeofencesPage.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  "pk.eyJ1IjoibWFwbGFwYW5lIiwiYSI6ImNtaDN5aGwybDFnY3AwOHFwbzJrOTF4c2cifQ.sQB_UMSe27x9JRdllElxmw";

export default function GeofencesPage() {
  const [geofences, setGeofences] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [newPolygon, setNewPolygon] = useState([]);
  const pendingLayerRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/v1/geofences`)
      .then((r) => r.json())
      .then(setGeofences)
      .catch(console.error);

    fetch(`${API_URL}/v1/vehicles`)
      .then((r) => r.json())
      .then(setVehicles)
      .catch(console.error);
  }, []);

  const saveGeofence = async (name, classification, description) => {
    try {
      await fetch(`${API_URL}/v1/geofences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, classification, description, polygon: newPolygon }),
      });
      fetch(`${API_URL}/v1/geofences`)
        .then((r) => r.json())
        .then(setGeofences);
    } catch (err) {
      console.error("Failed to save geofence:", err);
    } finally {
      setShowGeofenceModal(false);
      setNewPolygon([]);
    }
  };

  const handleAssignVehicle = async (vehicleId) => {
    if (!selected) return;
    await fetch(`${API_URL}/v1/geofences/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ geofenceId: selected.id, vehicleId }),
    });
    const res = await fetch(`${API_URL}/v1/geofences`);
    setGeofences(await res.json());
  };

  const handleUnassignVehicle = async (vehicleId) => {
    if (!selected) return;
    await fetch(`${API_URL}/v1/geofences/unassign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ geofenceId: selected.id, vehicleId }),
    });
    const res = await fetch(`${API_URL}/v1/geofences`);
    setGeofences(await res.json());
  };

  return (
    <div className="geofences-layout">
      <div className="left-panel">
        <div className="header">
          <h2>Geofences</h2>
          <button onClick={() => window.location.reload()} className="btn-secondary">
            Refresh
          </button>
        </div>

        <div className="geofence-list">
          {geofences.map((g) => (
            <div
              key={g.id}
              className={`geofence-item ${selected?.id === g.id ? "active" : ""}`}
              onClick={() => setSelected(g)}
            >
              <strong>{g.name}</strong>
              <p className="text-gray-500 text-xs">
                {g.classification || "Custom"} • {(g.vehicles || []).length} linked
              </p>
            </div>
          ))}
        </div>

        {selected && (
          <div className="vehicle-assign-box">
            <h3>Linked Vehicles</h3>
            <button onClick={() => setShowAssignModal(true)} className="btn-primary small">
              + Assign
            </button>
            <div className="assigned-list">
              {selected.vehicles?.length ? (
                selected.vehicles.map((v) => (
                  <div key={v.id} className="assigned-item">
                    <span>{v.registrationNumber || `Vehicle ${v.id}`}</span>
                    <button onClick={() => handleUnassignVehicle(v.id)}>×</button>
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500">No vehicles linked</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* === Map === */}
      <div className="map-panel">
        <MapContainer
          center={[-28.6, 24.657847]}
          zoom={6}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url={`https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
          />
          <DrawControl
            onPolygonCreated={(coords, layer) => {
              pendingLayerRef.current = layer;
              setNewPolygon(coords);
              setShowGeofenceModal(true);
            }}
          />
          {geofences.map((g) => (
            <Polygon
              key={g.id}
              positions={g.polygon}
              color={selected?.id === g.id ? "#2563eb" : "#f97316"}
              weight={selected?.id === g.id ? 3 : 1.5}
              opacity={0.7}
            />
          ))}
        </MapContainer>
      </div>

      {showGeofenceModal && (
        <GeofenceModal
          onSave={saveGeofence}
          onClose={() => {
            // remove temporary polygon if canceled
            if (pendingLayerRef.current) {
              pendingLayerRef.current.remove();
              pendingLayerRef.current = null;
            }
            setShowGeofenceModal(false);
          }}
        />
      )}
      {showAssignModal && (
        <AssignModal
          vehicles={vehicles}
          onAssign={handleAssignVehicle}
          onClose={() => setShowAssignModal(false)}
        />
      )}
    </div>
  );
}

/* ---------------------------
   Draw Control Component
--------------------------- */
function DrawControl({ onPolygonCreated }) {
  const map = useMap();

  useEffect(() => {
    if (!map || map._drawAdded) return;
    map._drawAdded = true;

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    const drawControl = new L.Control.Draw({
      position: "topleft",
      edit: { featureGroup: drawnItems },
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { color: "#2563eb", weight: 2 },
        },
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
    });

    map.addControl(drawControl);
    console.log("✅ Draw control added once");

    map.on(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      const latlngs = layer.getLatLngs()[0].map((p) => [p.lat, p.lng]);
      drawnItems.addLayer(layer);
      onPolygonCreated(latlngs, layer);
    });

    return () => {
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
      delete map._drawAdded;
    };
  }, [map, onPolygonCreated]);

  return null;
}

/* ---------------------------
   Modal Components
--------------------------- */
function GeofenceModal({ onSave, onClose }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [classification, setClassification] = useState("custom");

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <h3>Create Geofence</h3>

        <label>Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} />

        <label>Description</label>
        <textarea
          rows="3"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional short description..."
        />

        <label>Classification</label>
        <select value={classification} onChange={(e) => setClassification(e.target.value)}>
          <option value="custom">Custom</option>
          <option value="city">City</option>
          <option value="province">Province</option>
        </select>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            onClick={() => onSave(name, classification, description)}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignModal({ vehicles, onAssign, onClose }) {
  const [vehicleId, setVehicleId] = useState("");
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <h3>Assign Vehicle</h3>
        <label>Select Vehicle</label>
        <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
          <option value="">-- Select --</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>
              {v.registrationNumber || `Vehicle ${v.id}`}
            </option>
          ))}
        </select>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            onClick={() => {
              if (vehicleId) onAssign(vehicleId);
              onClose();
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
